// @ts-nocheck - untyped
import React from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine,
} from 'recharts';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import { useTheme } from '@mui/material/styles';
import { primitives } from '../../../theme';

import './ServicesMonitor.scss';

export function buildChartData(series) {
  if (!series || series.length === 0) {
    return { data: [], seriesConfig: [] };
  }

  const byKey = {};
  const defaultUnits = {
    rate: 'Count',
    latency: 'Milliseconds',
    errors: 'Count',
    saturation: 'Percent',
  };
  series.forEach(s => {
    const key = (s.metricName || '').replace(/\./g, '_');
    byKey[key] = {
      key,
      name: formatMetricLabel(s.metricName),
      unit: s.unit || defaultUnits[s.metricName] || '',
    };
  });
  const seriesConfig = GOLDEN_FOUR_ORDER.map(
    k =>
      byKey[k] || {
        key: k,
        name: GOLDEN_FOUR_LABELS[k] || k,
        unit: defaultUnits[k] || '',
      }
  );

  const timeToRow = {};
  series.forEach(s => {
    const key = (s.metricName || '').replace(/\./g, '_');
    (s.timestamps || []).forEach((ts, i) => {
      const time = formatTimestamp(ts);
      if (!timeToRow[time]) timeToRow[time] = { time };
      timeToRow[time][key] = s.values?.[i] ?? null;
    });
  });

  const data = Object.values(timeToRow).sort((a, b) => new Date(a.time) - new Date(b.time));
  return { data, seriesConfig };
}

const GOLDEN_FOUR_ORDER = ['rate', 'latency', 'errors', 'saturation'];
const GOLDEN_FOUR_LABELS = {
  rate: 'Rate (requests)',
  latency: 'Latency (ms)',
  errors: 'Errors',
  saturation: 'Saturation (error %)',
};

function formatMetricLabel(metricName) {
  if (!metricName) return '';
  if (GOLDEN_FOUR_LABELS[metricName]) return GOLDEN_FOUR_LABELS[metricName];
  if (metricName.includes('count')) return 'Requests';
  if (metricName.includes('avg')) return 'Latency (avg)';
  if (metricName.includes('percentile')) return 'Latency (p95)';
  if (metricName.includes('sum')) return 'Total time';
  return metricName;
}

function formatTimestamp(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

function formatYAxisTick(value) {
  if (value == null || Number.isNaN(value)) return '-';
  if (Math.abs(value) >= 1e6) return (value / 1e6).toFixed(1) + 'M';
  if (Math.abs(value) >= 1e3) return (value / 1e3).toFixed(1) + 'k';
  return Number.isInteger(value) ? value.toString() : value.toFixed(1);
}

export function getSeriesStats(data, dataKey) {
  if (!data?.length) return { min: null, max: null, avg: null, count: 0 };
  const values = data
    .map(d => d[dataKey])
    .filter(v => v != null && !Number.isNaN(v))
    .map(Number);
  if (values.length === 0) return { min: null, max: null, avg: null, count: 0 };
  const min = Math.min(...values);
  const max = Math.max(...values);
  const avg = values.reduce((a, b) => a + b, 0) / values.length;
  return { min, max, avg, count: values.length };
}

const SERIES_COLORS = [
  primitives.clarivatePurple[500],
  primitives.clarivatePurple[300],
  primitives.clarivatePurple[400],
  primitives.clarivatePurple[600],
];

function useSeriesColors() {
  const theme = useTheme();
  return [...SERIES_COLORS, theme.tokens.success, theme.tokens.danger];
}

const styles = {
  chartPaperGrid: 'sm-grid-chart-paper',
  chartPaper: 'sm-chart-paper',
};

export function SparklineCard({ config, data, colorIndex = 0 }) {
  const seriesColors = useSeriesColors();
  const strokeColor = seriesColors[colorIndex % seriesColors.length];
  if (!data?.length) return null;
  return (
    <ResponsiveContainer width="100%" height={88}>
      <LineChart data={data} margin={{ top: 4, right: 4, left: 4, bottom: 4 }}>
        <Line
          type="monotone"
          dataKey={config.key}
          stroke={strokeColor}
          strokeWidth={2}
          dot={false}
          connectNulls
        />
      </LineChart>
    </ResponsiveContainer>
  );
}

function formatTooltipValue(value, unit) {
  if (value == null || Number.isNaN(value)) return '-';
  const formatted = Number.isInteger(value) ? value.toLocaleString() : value.toFixed(2);
  return unit ? `${formatted} ${unit}` : formatted;
}

function formatStat(v, unit) {
  if (v == null || Number.isNaN(v)) return '-';
  const s = Number.isInteger(v) ? v.toLocaleString() : v.toFixed(2);
  return unit ? `${s} ${unit}` : s;
}

export function ChartCard({
  config,
  data,
  title,
  useGridStyle,
  styles,
  colorIndex = 0,
  hideTitle = false,
  noPaper = false,
  chartHeight,
}) {
  const theme = useTheme();
  const axisColor = theme.tokens.fgSecondary;
  const legendColor = theme.tokens.fgPrimary;
  const refLineLabelColor = theme.tokens.fgTertiary;
  const tooltipBg = theme.tokens.surface;
  const tooltipBorder = theme.tokens.accentBorder;
  const tooltipLabelColor = theme.tokens.fgPrimary;
  const tooltipItemColor = theme.tokens.fgSecondary;
  const gridStroke = theme.tokens.accentSubtle;

  const seriesColors = useSeriesColors();
  const paperClass = useGridStyle ? styles.chartPaperGrid : styles.chartPaper;
  const strokeColor = seriesColors[colorIndex % seriesColors.length];
  const stats = getSeriesStats(data, config.key);
  const showStats = stats.count > 0;
  const height = chartHeight ?? (hideTitle ? 220 : 268);

  const content = (
    <>
      {!hideTitle && (
        <>
          <Typography className="sm-chart-title" variant="subtitle1">
            {title ? `${title} - ${config.name}` : config.name}
            {config.unit && (
              <Typography
                component="span"
                variant="caption"
                sx={{ ml: 1, color: 'text.secondary' }}
              >
                ({config.unit})
              </Typography>
            )}
          </Typography>
          {showStats && (
            <Typography className="sm-chart-stats-strip" component="div" variant="caption">
              Min: {formatStat(stats.min, config.unit)} · Max: {formatStat(stats.max, config.unit)}{' '}
              · Avg: {formatStat(stats.avg, config.unit)} · {stats.count} points
            </Typography>
          )}
        </>
      )}
      <ResponsiveContainer width="100%" height={height}>
        <LineChart data={data} margin={{ top: 8, right: 26, left: -8, bottom: 8 }}>
          <CartesianGrid strokeDasharray="3 3" stroke={gridStroke} vertical={false} />
          <XAxis dataKey="time" tick={{ fontSize: 11, fill: axisColor }} stroke={axisColor} />
          <YAxis
            tick={{ fontSize: 11, fill: axisColor }}
            stroke={axisColor}
            tickFormatter={formatYAxisTick}
            width={42}
            domain={
              config.key === 'errors'
                ? [0, Math.max(10, (stats.max ?? 0) + 1)]
                : config.key === 'saturation'
                  ? [0, Math.max(10, (stats.max ?? 0) + 1)]
                  : undefined
            }
          />
          <Tooltip
            contentStyle={{
              borderRadius: 12,
              backgroundColor: tooltipBg,
              border: `1px solid ${tooltipBorder}`,
              boxShadow: '0 8px 24px rgba(94, 51, 191, 0.12)',
              padding: '14px 18px',
              minWidth: 200,
            }}
            labelStyle={{
              fontWeight: 700,
              color: tooltipLabelColor,
              marginBottom: 8,
              fontSize: 13,
            }}
            formatter={value => [formatTooltipValue(value, config.unit), config.name]}
            labelFormatter={label => (label ? `Time: ${label}` : '')}
            itemStyle={{ fontWeight: 600, color: tooltipItemColor }}
          />
          {showStats && stats.avg != null && (
            <ReferenceLine
              y={stats.avg}
              stroke={strokeColor}
              strokeDasharray="4 4"
              strokeOpacity={0.7}
              label={{ value: 'Avg', position: 'right', fontSize: 10, fill: refLineLabelColor }}
            />
          )}
          <Legend
            wrapperStyle={{ paddingTop: 10 }}
            formatter={() => (
              <span style={{ fontSize: 13, color: legendColor, fontWeight: 500 }}>
                {config.name}
              </span>
            )}
            iconType="line"
            iconSize={12}
          />
          <Line
            type="monotone"
            dataKey={config.key}
            name={config.name}
            stroke={strokeColor}
            strokeWidth={2.8}
            dot={{ r: 2.5, fill: strokeColor, strokeWidth: 0 }}
            activeDot={{ r: 5, fill: strokeColor, stroke: theme.tokens.surface, strokeWidth: 2 }}
            connectNulls
          />
        </LineChart>
      </ResponsiveContainer>
    </>
  );
  if (noPaper) return content;
  return (
    <Paper className={paperClass} elevation={0}>
      {content}
    </Paper>
  );
}

export default function MetricsCharts({ series = [], metadata = {}, title, gridItem = false }) {
  const { data, seriesConfig } = buildChartData(series);
  const hasData = data.length > 0 && seriesConfig.some(c => data.some(d => d[c.key] != null));

  if (!hasData) {
    const emptyPaper = (
      <Paper className={gridItem ? styles.chartPaperGrid : styles.chartPaper} elevation={0}>
        <Typography className="sm-chart-title" variant="subtitle1">
          {title || 'Metrics'}
        </Typography>
        <Box className="sm-chart-empty">
          <Typography variant="body1" sx={{ fontWeight: 500 }}>
            No data for the selected period.
          </Typography>
          <Typography variant="body2" sx={{ mt: 1, opacity: 0.9 }}>
            Try another time range or generate some traffic.
          </Typography>
          {metadata?.dataPointCount === 0 && (
            <Typography variant="body2" sx={{ mt: 1.5, fontStyle: 'italic' }}>
              Data points: 0. Metrics may take 1-2 minutes to appear in CloudWatch.
            </Typography>
          )}
        </Box>
      </Paper>
    );
    if (gridItem) {
      return (
        <Grid item xs={12} md={6}>
          {emptyPaper}
        </Grid>
      );
    }
    return emptyPaper;
  }

  const chartNodes = seriesConfig.map((config, idx) => (
    <ChartCard
      key={config.key}
      config={config}
      data={data}
      title={title}
      useGridStyle={gridItem}
      styles={styles}
      colorIndex={idx}
    />
  ));

  if (gridItem) {
    return (
      <>
        {seriesConfig.map((config, idx) => (
          <Grid item xs={12} md={6} key={config.key}>
            <ChartCard
              config={config}
              data={data}
              title={title}
              useGridStyle
              styles={styles}
              colorIndex={idx}
            />
          </Grid>
        ))}
      </>
    );
  }

  return <>{chartNodes}</>;
}
